import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiDelete,
  FiFilter,
  FiList,
  FiMinus,
  FiPackage,
  FiPercent,
  FiPlus,
  FiTag,
  FiX,
} from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import {
  applyPurchaseOrderItemsDiscount,
  addPurchaseOrderItems,
  getPurchaseOrderById,
  getPurchaseOrderCatalog,
  getPurchaseOrderReasons,
  listPurchaseOrders,
  convertPurchaseOrderToSale,
  updatePurchaseOrderDiscount,
  updatePurchaseOrderItemAdjustment,
  updatePurchaseOrderItemDiscount,
  updatePurchaseOrderItemConfirmation,
  updatePurchaseOrderStatus,
} from '../api';
import DiscountModal from '../components/DiscountModal';
import DocumentTabs from '../components/DocumentTabs';
import InlineLoading from '../components/InlineLoading';
import OrderDiscountModal from '../components/OrderDiscountModal';
import PurchaseOrderCatalogEditor from '../components/PurchaseOrderCatalogEditor';
import useCurrentUser from '../hooks/useCurrentUser';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useMyPreferences from '../hooks/useMyPreferences';
import usePurchaseEvents from '../hooks/usePurchaseEvents';
import { notify } from '../utils/alertBus';
import { normalizePaginatedResponse } from '../utils/pagination';
import { hasPermission } from '../utils/permissions';
import { normalizePurchaseOrderReview } from '../utils/userPreferences';
import {
  PURCHASE_ORDER_STATUS_OPTIONS,
  PURCHASE_ORDER_STATUS_PREPARING,
  getCanonicalPurchaseOrderStatus,
  getPurchaseOrderActionLabel,
  getPurchaseOrderItemActionLabel,
  getPurchaseOrderItemActionTone,
  getPurchaseOrderStatusLabel,
  getPurchaseOrderStatusTone,
  isPurchaseOrderPreparingStatus,
  normalizePurchaseOrderAction,
} from '../utils/purchaseOrderStatus';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;
const PENDING_ORDER_TAB_STATUS = PURCHASE_ORDER_STATUS_PREPARING;
const PURCHASE_ORDER_REALTIME_EVENTS = new Set([
  'purchase-order-updated',
  'purchase-order-status-updated',
  'purchase-order-sale-created',
]);

const getUnitDiscount = (item) => {
  const discount = Number(item?.discount || 0);
  return Number.isFinite(discount) && discount > 0 ? discount : 0;
};

const getDiscountedUnitPrice = (item) =>
  Math.max((Number(item?.price) || 0) - getUnitDiscount(item), 0);

const getItemSubtotalBeforeDiscount = (item) => {
  const quantity = Number(item?.currentQuantity ?? item?.confirmedQuantity ?? item?.requestedQuantity ?? 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;

  const price = Number(item?.price || 0);
  if (!Number.isFinite(price) || price <= 0) return 0;

  return Math.floor(price * quantity);
};

const getItemDiscountTotal = (item) => {
  const unitDiscount = getUnitDiscount(item);
  if (unitDiscount <= 0) return 0;

  const quantity = Number(item?.currentQuantity ?? item?.confirmedQuantity ?? item?.requestedQuantity ?? 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;

  const price = Number(item?.price || 0);
  if (!Number.isFinite(price) || price <= 0) return 0;

  const fullSubtotal = getItemSubtotalBeforeDiscount(item);
  const hasCurrentSubtotal = item?.currentSubtotal !== null
    && item?.currentSubtotal !== undefined
    && Number.isFinite(Number(item.currentSubtotal));
  const discountedSubtotal = hasCurrentSubtotal
    ? Number(item.currentSubtotal)
    : Math.floor(Math.max(price - unitDiscount, 0) * quantity);

  return Math.max(fullSubtotal - discountedSubtotal, 0);
};

const formatQuantity = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-CL');
};

const getItemImage = (item) =>
  item?.priceImage || item?.varietyImage || item?.productImage || 'https://placehold.co/96';

const normalizeOrderTabStatusFilters = (statuses = []) => {
  const availableStatuses = new Set(PURCHASE_ORDER_STATUS_OPTIONS.map((option) => option.value));
  const filters = new Set([PENDING_ORDER_TAB_STATUS]);
  statuses.forEach((status) => {
    const normalized = getCanonicalPurchaseOrderStatus(status);
    if (availableStatuses.has(normalized)) filters.add(normalized);
  });
  return Array.from(filters);
};

const getOrderTabId = (orderId) => String(orderId || '').trim();

const shouldShowOrderInTabs = ({ order, statusFilters, pinnedOrderIds }) => {
  if (!order?.id) return false;
  const orderId = getOrderTabId(order.id);
  if (pinnedOrderIds.includes(orderId)) return true;
  return statusFilters.includes(getCanonicalPurchaseOrderStatus(order.status));
};

const sortPendingOrderTabs = (orders) => [...orders].sort((a, b) => {
  const dateDiff = new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
  if (dateDiff !== 0) return dateDiff;
  return Number(b.id || 0) - Number(a.id || 0);
});

const buildPendingOrderTabMeta = (order) => {
  const clientName = order?.Client?.name || 'Cliente';
  const total = formatMoney(order?.currentTotalAmount);
  const itemCount = Number(order?.itemCount || 0);
  const confirmedItemCount = Number(order?.confirmedItemCount || 0);
  return `${clientName} · ${total} · ${confirmedItemCount}/${itemCount}`;
};

const formatQuantityInput = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const normalized = String(value).replace(',', '.').trim();
  if (!normalized) return '';
  if (normalized.endsWith('.')) return normalized;
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return '';
  return amount.toFixed(3).replace(/\.?0+$/, '');
};

const sanitizeQuantityInput = (value) => {
  const normalized = String(value ?? '').replace(',', '.');
  if (!normalized.trim()) return '';
  const digitsOnly = normalized.replace(/[^\d.]/g, '');
  const [integerPart, ...decimalParts] = digitsOnly.split('.');
  const decimals = decimalParts.join('').slice(0, 3);
  if (!integerPart && !decimals && normalized.includes('.')) return '0.';
  if (!decimalParts.length) return integerPart.replace(/^0+(?=\d)/, '') || '0';
  const cleanInteger = integerPart.replace(/^0+(?=\d)/, '') || '0';
  if (normalized.endsWith('.') && decimals.length === 0) return `${cleanInteger}.`;
  return decimals ? `${cleanInteger}.${decimals}` : cleanInteger;
};

const resolveQuantityAction = (item, quantityValue) => {
  const requestedQuantity = Number(item?.requestedQuantity || 0);
  const quantity = Number(quantityValue);
  if (!Number.isFinite(quantity)) return 'accept';
  if (quantity <= 0) return 'remove';
  if (Math.abs(quantity - requestedQuantity) < 0.0001) return 'accept';
  return quantity < requestedQuantity ? 'reduce' : 'increase';
};

const buildDefaultDraft = (item) => ({
  action: item?.confirmationAction || resolveQuantityAction(item, item?.confirmedQuantity ?? item?.currentQuantity ?? item?.requestedQuantity),
  confirmedQuantity: formatQuantityInput(item?.confirmedQuantity ?? item?.currentQuantity ?? item?.requestedQuantity ?? ''),
  reasonId: item?.adjustmentReasonId || '',
});

const isDraftReadyToSave = (item, draft) => {
  const action = draft?.action || 'accept';
  const requestedQuantity = Number(item?.requestedQuantity || 0);
  if (action === 'accept') return true;
  if (action === 'remove') return Boolean(draft?.reasonId);
  if (action === 'reduce' || action === 'increase') {
    const confirmedQuantity = Number(draft?.confirmedQuantity);
    if (!Number.isFinite(confirmedQuantity) || confirmedQuantity <= 0 || !draft?.reasonId) return false;
    if (action === 'reduce') return confirmedQuantity < requestedQuantity;
    return confirmedQuantity > requestedQuantity;
  }
  return false;
};

const buildItemSaveSignature = (item, draft) => {
  const action = draft?.action || 'accept';
  const quantity = action === 'remove'
    ? 0
    : action === 'accept'
      ? Number(item?.requestedQuantity || 0)
      : Number(draft?.confirmedQuantity || 0);
  const normalizedQuantity = Number.isFinite(quantity) ? quantity.toFixed(3) : 'invalid';
  const reasonId = action === 'accept' ? '' : (draft?.reasonId || '');
  return `${action}|${normalizedQuantity}|${reasonId}`;
};

const isAcceptWithoutAdjustment = (item, draft) => {
  const action = draft?.action || resolveQuantityAction(item, draft?.confirmedQuantity);
  if (action !== 'accept') return false;
  const requestedQuantity = Number(item?.requestedQuantity || 0);
  const confirmedQuantity = Number(draft?.confirmedQuantity);
  return Number.isFinite(confirmedQuantity)
    && Math.abs(confirmedQuantity - requestedQuantity) < 0.0001;
};

const historyEventLabel = (eventType) => {
  if (eventType === 'order_created') return 'Orden creada';
  if (eventType === 'status_changed') return 'Estado actualizado';
  if (eventType === 'item_adjustment_saved') return 'Cambios de item guardados';
  if (eventType === 'item_confirmed') return 'Item confirmado';
  if (eventType === 'item_discount_changed') return 'Descuento de item';
  if (eventType === 'order_discount_changed') return 'Descuento total';
  if (eventType === 'order_edited_by_client') return 'Orden editada por cliente';
  if (eventType === 'item_added') return 'Producto agregado';
  if (eventType === 'item_quantity_added') return 'Cantidad agregada';
  if (eventType === 'sale_created') return 'Venta generada';
  return eventType || 'Evento';
};

const formatHistoryAction = (action) => (action ? getPurchaseOrderActionLabel(action) : 'Pendiente');

const formatHistoryStatus = (status) => (status ? getPurchaseOrderStatusLabel(status) : '-');

const historyActorName = (entry) =>
  entry.ActorUser?.name
  || entry.ActorUser?.username
  || entry.ActorClient?.name
  || entry.actorType
  || 'Sistema';

const HistoryMetric = ({ label, previous, next }) => (
  <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
    <p className="text-[11px] uppercase text-gray-500">{label}</p>
    <p className="mt-1 text-xs text-gray-700">
      <span className="text-gray-500">{previous ?? '-'}</span>
      <span className="px-1 text-gray-400">-&gt;</span>
      <span className="font-semibold text-gray-900">{next ?? '-'}</span>
    </p>
  </div>
);

const renderHistoryDetails = (entry) => {
  if (entry.eventType === 'status_changed') {
    return (
      <div className="mt-2 grid grid-cols-1 gap-2">
        <HistoryMetric
          label="Estado"
          previous={formatHistoryStatus(entry.previousValue?.status)}
          next={formatHistoryStatus(entry.newValue?.status)}
        />
      </div>
    );
  }

  if (entry.eventType === 'item_confirmed' || entry.eventType === 'item_adjustment_saved') {
    const previous = entry.previousValue || {};
    const next = entry.newValue || {};
    const metadata = entry.metadata || {};
    const previousOrder = metadata.previousOrder || {};
    const newOrder = metadata.newOrder || {};
    return (
      <div className="mt-2 space-y-2">
        <div>
          <p className="text-xs font-semibold text-gray-900">{metadata.productName || 'Item'}</p>
          {metadata.varietyName && <p className="text-[11px] text-gray-500">{metadata.varietyName}</p>}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
          <HistoryMetric
            label="Accion"
            previous={formatHistoryAction(previous.confirmationAction)}
            next={formatHistoryAction(next.confirmationAction)}
          />
          <HistoryMetric
            label="Cantidad"
            previous={formatQuantity(previous.currentQuantity ?? metadata.requestedQuantity)}
            next={formatQuantity(next.currentQuantity)}
          />
          <HistoryMetric
            label="Subtotal"
            previous={formatMoney(previous.currentSubtotal)}
            next={formatMoney(next.currentSubtotal)}
          />
          <HistoryMetric
            label="Total orden"
            previous={formatMoney(previousOrder.currentTotalAmount)}
            next={formatMoney(newOrder.currentTotalAmount)}
          />
        </div>
        {entry.reason?.label && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
            Motivo: {entry.reason.label}
          </p>
        )}
        {metadata.unitPrice !== undefined && (
          <p className="text-[11px] text-gray-500">Precio unitario: {formatMoney(metadata.unitPrice)}</p>
        )}
      </div>
    );
  }

  if (entry.eventType === 'order_created') {
    return (
      <div className="mt-2 grid grid-cols-1 gap-2">
        <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
          Estado inicial: <span className="font-semibold">{formatHistoryStatus(entry.newValue?.status)}</span>
          {entry.newValue?.itemCount !== undefined && (
            <span className="ml-2">Items: <span className="font-semibold">{entry.newValue.itemCount}</span></span>
          )}
          {entry.newValue?.totalAmount !== undefined && (
            <span className="ml-2">Total: <span className="font-semibold">{formatMoney(entry.newValue.totalAmount)}</span></span>
          )}
        </div>
      </div>
    );
  }

  if (entry.eventType === 'item_discount_changed') {
    const previous = entry.previousValue || {};
    const next = entry.newValue || {};
    const metadata = entry.metadata || {};
    const previousOrder = metadata.previousOrder || {};
    const newOrder = metadata.newOrder || {};
    return (
      <div className="mt-2 space-y-2">
        <div>
          <p className="text-xs font-semibold text-gray-900">{metadata.productName || 'Item'}</p>
          {metadata.varietyName && <p className="text-[11px] text-gray-500">{metadata.varietyName}</p>}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
          <HistoryMetric
            label="Desc. unitario"
            previous={formatMoney(previous.discount)}
            next={formatMoney(next.discount)}
          />
          <HistoryMetric
            label="Subtotal"
            previous={formatMoney(previous.currentSubtotal)}
            next={formatMoney(next.currentSubtotal)}
          />
          <HistoryMetric
            label="Total orden"
            previous={formatMoney(previousOrder.currentTotalAmount)}
            next={formatMoney(newOrder.currentTotalAmount)}
          />
        </div>
      </div>
    );
  }

  if (entry.eventType === 'order_discount_changed') {
    const previous = entry.previousValue || {};
    const next = entry.newValue || {};
    return (
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
        <HistoryMetric
          label="Desc. total"
          previous={formatMoney(previous.orderDiscountAmount)}
          next={formatMoney(next.orderDiscountAmount)}
        />
        <HistoryMetric
          label="Total orden"
          previous={formatMoney(previous.currentTotalAmount)}
          next={formatMoney(next.currentTotalAmount)}
        />
      </div>
    );
  }

  if (entry.eventType === 'order_edited_by_client') {
    const previous = entry.previousValue || {};
    const next = entry.newValue || {};
    return (
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
        <HistoryMetric
          label="Items"
          previous={previous.itemCount}
          next={next.itemCount}
        />
        <HistoryMetric
          label="Total"
          previous={formatMoney(previous.currentTotalAmount)}
          next={formatMoney(next.currentTotalAmount)}
        />
      </div>
    );
  }

  if (entry.eventType === 'item_added' || entry.eventType === 'item_quantity_added') {
    const metadata = entry.metadata || {};
    const previousOrder = metadata.previousOrder || {};
    const newOrder = metadata.newOrder || {};
    return (
      <div className="mt-2 space-y-2">
        <div>
          <p className="text-xs font-semibold text-gray-900">{metadata.productName || 'Item'}</p>
          {metadata.varietyName && <p className="text-[11px] text-gray-500">{metadata.varietyName}</p>}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
          <HistoryMetric
            label="Agregado"
            previous="-"
            next={formatQuantity(metadata.addedQuantity)}
          />
          <HistoryMetric
            label="Total orden"
            previous={formatMoney(previousOrder.currentTotalAmount)}
            next={formatMoney(newOrder.currentTotalAmount)}
          />
        </div>
      </div>
    );
  }

  if (entry.eventType === 'sale_created') {
    return (
      <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
        Venta #{entry.newValue?.purchaseId || entry.metadata?.purchaseId || '-'}
        {entry.newValue?.totalAmount !== undefined && (
          <span className="ml-2">Total: <span className="font-semibold">{formatMoney(entry.newValue.totalAmount)}</span></span>
        )}
      </div>
    );
  }

  return null;
};

const PurchaseOrderDetailPage = () => {
  const { branchId, id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const {
    preferences: myPreferences,
  } = useMyPreferences();
  const canCreateSale = hasPermission(user, 'sales.create');
  const [order, setOrder] = useState(null);
  const [reasons, setReasons] = useState({ reduce: [], remove: [], increase: [] });
  const [drafts, setDrafts] = useState({});
  const [itemSaveStates, setItemSaveStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [convertingSale, setConvertingSale] = useState(false);
  const [activeTab, setActiveTab] = useState('review');
  const [numericPadItemId, setNumericPadItemId] = useState(null);
  const [numericPadValue, setNumericPadValue] = useState('');
  const [discountItem, setDiscountItem] = useState(null);
  const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false);
  const [showAllItemsDiscountModal, setShowAllItemsDiscountModal] = useState(false);
  const [allItemsDiscountMode, setAllItemsDiscountMode] = useState('amount');
  const [allItemsDiscountRaw, setAllItemsDiscountRaw] = useState('0');
  const [discountSaving, setDiscountSaving] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [addItemsCatalog, setAddItemsCatalog] = useState([]);
  const [addItemsLoading, setAddItemsLoading] = useState(false);
  const [addItemsError, setAddItemsError] = useState('');
  const [pendingOrderTabs, setPendingOrderTabs] = useState([]);
  const [pendingOrdersLoading, setPendingOrdersLoading] = useState(false);
  const [pendingOrdersError, setPendingOrdersError] = useState('');
  const [tabStatusFilters, setTabStatusFilters] = useState([PENDING_ORDER_TAB_STATUS]);
  const [pinnedOrderIds, setPinnedOrderIds] = useState([]);
  const [allOrderOptions, setAllOrderOptions] = useState([]);
  const [allOrdersLoading, setAllOrdersLoading] = useState(false);
  const [allOrdersError, setAllOrdersError] = useState('');
  const [orderTabSearch, setOrderTabSearch] = useState('');
  const [showTabFilters, setShowTabFilters] = useState(false);
  const autoSaveTimersRef = useRef({});
  const inFlightSignaturesRef = useRef({});
  const lastSavedSignaturesRef = useRef({});
  const queuedDraftsRef = useRef({});
  const localMutationUntilRef = useRef(0);
  const externalRefreshTimerRef = useRef(null);
  const externalRefreshInFlightRef = useRef(false);
  const externalRefreshQueuedRef = useRef(false);
  const tabFiltersRef = useRef(null);

  const purchaseOrderReview = useMemo(
    () => normalizePurchaseOrderReview(myPreferences?.purchaseOrderReview),
    [myPreferences?.purchaseOrderReview]
  );
  const autoSaveItemChanges = purchaseOrderReview.autoSaveItemChanges;
  const requireItemConfirmation = purchaseOrderReview.requireItemConfirmation;

  const activeOrderTabStatusFilters = useMemo(
    () => normalizeOrderTabStatusFilters(tabStatusFilters),
    [tabStatusFilters]
  );

  const filteredAllOrderOptions = useMemo(() => {
    const search = orderTabSearch.trim().toLowerCase();
    if (!search) return allOrderOptions;
    return allOrderOptions.filter((entry) => {
      const haystack = [
        entry.id,
        entry.Client?.name,
        entry.Client?.rut,
        getPurchaseOrderStatusLabel(entry.status),
        entry.currentTotalAmount,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(search.replace(/^#/, ''));
    });
  }, [allOrderOptions, orderTabSearch]);

  const items = useMemo(
    () => [...(order?.Items || [])].sort((a, b) => Number(a.lineIndex || 0) - Number(b.lineIndex || 0)),
    [order]
  );
  const productDiscountTotal = useMemo(
    () => items.reduce((sum, item) => sum + getItemDiscountTotal(item), 0),
    [items]
  );
  const itemsSubtotalBeforeDiscount = useMemo(
    () => items.reduce((sum, item) => sum + getItemSubtotalBeforeDiscount(item), 0),
    [items]
  );
  const orderDiscountAmount = Number(order?.orderDiscountAmount || 0);
  const totalDiscountAmount = productDiscountTotal + orderDiscountAmount;
  const computedCurrentTotalAmount = Math.max(itemsSubtotalBeforeDiscount - totalDiscountAmount, 0);
  const discountBreakdown = [
    productDiscountTotal > 0 ? `Productos -${formatMoney(productDiscountTotal)}` : null,
    orderDiscountAmount > 0
      ? `Orden -${formatMoney(orderDiscountAmount)}${order?.orderDiscountPercentage ? ` (${order.orderDiscountPercentage}%)` : ''}`
      : null,
  ].filter(Boolean);

  const setItemSaveState = (itemId, patch) => {
    setItemSaveStates((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || { status: 'idle', message: '' }),
        ...patch,
      },
    }));
  };

  const clearAutoSaveTimer = (itemId) => {
    if (autoSaveTimersRef.current[itemId]) {
      clearTimeout(autoSaveTimersRef.current[itemId]);
      delete autoSaveTimersRef.current[itemId];
    }
  };

  const syncSavedSignaturesFromOrder = useCallback((sourceOrder) => {
    Object.values(autoSaveTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    autoSaveTimersRef.current = {};
    inFlightSignaturesRef.current = {};
    queuedDraftsRef.current = {};
    const nextSignatures = {};
    const nextStates = {};
    (sourceOrder?.Items || []).forEach((item) => {
      const confirmationStatus = String(item.confirmationStatus || '').trim().toLowerCase();
      if (confirmationStatus === 'confirmed' || confirmationStatus === 'saved') {
        nextSignatures[item.id] = buildItemSaveSignature(item, buildDefaultDraft(item));
      }
      nextStates[item.id] = {
        status: confirmationStatus === 'confirmed'
          ? 'confirmed'
          : confirmationStatus === 'saved'
            ? 'saved'
            : 'idle',
        message: '',
      };
    });
    lastSavedSignaturesRef.current = nextSignatures;
    setItemSaveStates(nextStates);
  }, []);

  const loadOrder = useCallback(async () => {
    if (!branchId || !id) return;
    setLoading(true);
    setError('');
    try {
      const [orderData, reasonData] = await Promise.all([
        getPurchaseOrderById(branchId, id),
        getPurchaseOrderReasons(branchId),
      ]);
      setOrder(orderData);
      setReasons({
        reduce: Array.isArray(reasonData?.reduce) ? reasonData.reduce : [],
        remove: Array.isArray(reasonData?.remove) ? reasonData.remove : [],
        increase: Array.isArray(reasonData?.increase) ? reasonData.increase : [],
      });
      setDrafts(
        (orderData?.Items || []).reduce((acc, item) => {
          acc[item.id] = buildDefaultDraft(item);
          return acc;
        }, {})
      );
      syncSavedSignaturesFromOrder(orderData);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'No se pudo cargar la orden.');
    } finally {
      setLoading(false);
    }
  }, [branchId, id, syncSavedSignaturesFromOrder]);

  const loadPendingOrderTabs = useCallback(async () => {
    if (!branchId) return;
    setPendingOrdersLoading(true);
    setPendingOrdersError('');
    try {
      const [statusResponses, pinnedResponses] = await Promise.all([
        Promise.all(activeOrderTabStatusFilters.map((status) =>
          listPurchaseOrders(branchId, {
            status,
            pageSize: 0,
          })
        )),
        Promise.all(pinnedOrderIds.map(async (orderId) => {
          try {
            return await getPurchaseOrderById(branchId, orderId);
          } catch (err) {
            console.error('Error loading pinned purchase order tab:', err);
            return null;
          }
        })),
      ]);
      const merged = new Map();
      statusResponses.forEach((response) => {
        const normalized = normalizePaginatedResponse(response);
        (normalized.data || []).forEach((entry) => {
          if (shouldShowOrderInTabs({
            order: entry,
            statusFilters: activeOrderTabStatusFilters,
            pinnedOrderIds,
          })) {
            merged.set(Number(entry.id), entry);
          }
        });
      });
      pinnedResponses.filter(Boolean).forEach((entry) => {
        merged.set(Number(entry.id), {
          ...merged.get(Number(entry.id)),
          ...entry,
          isPinnedTab: true,
        });
      });
      setPendingOrderTabs(sortPendingOrderTabs(Array.from(merged.values())));
    } catch (err) {
      console.error(err);
      setPendingOrdersError(err?.response?.data?.message || 'No se pudieron cargar ordenes pendientes.');
    } finally {
      setPendingOrdersLoading(false);
    }
  }, [activeOrderTabStatusFilters, branchId, pinnedOrderIds]);

  const loadAllOrderOptions = useCallback(async () => {
    if (!branchId) return;
    setAllOrdersLoading(true);
    setAllOrdersError('');
    try {
      const response = await listPurchaseOrders(branchId, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(response);
      setAllOrderOptions(sortPendingOrderTabs(normalized.data || []));
    } catch (err) {
      console.error(err);
      setAllOrdersError(err?.response?.data?.message || 'No se pudieron cargar las ordenes.');
    } finally {
      setAllOrdersLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    loadPendingOrderTabs();
  }, [loadPendingOrderTabs]);

  useEffect(() => {
    if (showTabFilters) loadAllOrderOptions();
  }, [loadAllOrderOptions, showTabFilters]);

  useEffect(() => {
    if (!showTabFilters) return undefined;
    const handleClick = (event) => {
      if (tabFiltersRef.current && !tabFiltersRef.current.contains(event.target)) {
        setShowTabFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTabFilters]);

  useEffect(() => () => {
    Object.values(autoSaveTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    autoSaveTimersRef.current = {};
    if (externalRefreshTimerRef.current) {
      clearTimeout(externalRefreshTimerRef.current);
      externalRefreshTimerRef.current = null;
    }
  }, []);

  const applyOrderResponse = (response) => {
    const nextOrder = response?.purchaseOrder || response;
    if (!nextOrder) return null;
    setOrder(nextOrder);
    setPendingOrderTabs((prev) => {
      const nextMap = new Map(prev.map((entry) => [Number(entry.id), entry]));
      if (shouldShowOrderInTabs({
        order: nextOrder,
        statusFilters: activeOrderTabStatusFilters,
        pinnedOrderIds,
      })) {
        nextMap.set(Number(nextOrder.id), {
          ...nextMap.get(Number(nextOrder.id)),
          ...nextOrder,
          isPinnedTab: pinnedOrderIds.includes(getOrderTabId(nextOrder.id)),
        });
      } else {
        nextMap.delete(Number(nextOrder.id));
      }
      return sortPendingOrderTabs(Array.from(nextMap.values()));
    });
    setDrafts((prevDrafts) => (
      (nextOrder.Items || []).reduce((acc, item) => {
        acc[item.id] = prevDrafts[item.id] || buildDefaultDraft(item);
        return acc;
      }, {})
    ));
    return nextOrder;
  };

  const replaceOrderFromRealtime = useCallback((nextOrder) => {
    if (!nextOrder) return;
    setOrder(nextOrder);
    setPendingOrderTabs((prev) => {
      const nextMap = new Map(prev.map((entry) => [Number(entry.id), entry]));
      if (shouldShowOrderInTabs({
        order: nextOrder,
        statusFilters: activeOrderTabStatusFilters,
        pinnedOrderIds,
      })) {
        nextMap.set(Number(nextOrder.id), {
          ...nextMap.get(Number(nextOrder.id)),
          ...nextOrder,
          isPinnedTab: pinnedOrderIds.includes(getOrderTabId(nextOrder.id)),
        });
      } else {
        nextMap.delete(Number(nextOrder.id));
      }
      return sortPendingOrderTabs(Array.from(nextMap.values()));
    });
    setDrafts(
      (nextOrder.Items || []).reduce((acc, item) => {
        acc[item.id] = buildDefaultDraft(item);
        return acc;
      }, {})
    );
    syncSavedSignaturesFromOrder(nextOrder);
  }, [activeOrderTabStatusFilters, pinnedOrderIds, syncSavedSignaturesFromOrder]);

  const markLocalPurchaseOrderMutation = useCallback(() => {
    localMutationUntilRef.current = Date.now() + 6000;
  }, []);

  const isOwnRealtimeEvent = useCallback((event) => {
    const actorType = String(event?.actorType || '');
    if (actorType === 'user') {
      return user?.id != null && String(event.actorUserId || '') === String(user.id);
    }
    if (actorType) return false;
    return Date.now() < localMutationUntilRef.current;
  }, [user?.id]);

  const buildRealtimeUpdateMessage = useCallback((event) => {
    if (event?.type === 'purchase-order-sale-created') {
      return 'Esta orden fue convertida a venta por otro usuario.';
    }
    if (event?.actorType === 'client') {
      return 'El cliente actualizo esta orden. Cambios aplicados.';
    }
    return 'Otro usuario actualizo esta orden. Cambios aplicados.';
  }, []);

  const refreshOrderFromRealtime = useCallback(async (event) => {
    if (!branchId || !id) return;
    if (externalRefreshInFlightRef.current) {
      externalRefreshQueuedRef.current = true;
      return;
    }
    externalRefreshInFlightRef.current = true;
    try {
      const nextOrder = await getPurchaseOrderById(branchId, id);
      replaceOrderFromRealtime(nextOrder);
      notify(buildRealtimeUpdateMessage(event), { duration: 4000 });
      loadPendingOrderTabs();
    } catch (err) {
      console.error('Error refreshing purchase order from realtime event:', err);
      notify('La orden cambio, pero no se pudo actualizar en pantalla.', { type: 'error', duration: 5000 });
    } finally {
      externalRefreshInFlightRef.current = false;
      if (externalRefreshQueuedRef.current) {
        externalRefreshQueuedRef.current = false;
        externalRefreshTimerRef.current = setTimeout(() => {
          externalRefreshTimerRef.current = null;
          refreshOrderFromRealtime(event);
        }, 250);
      }
    }
  }, [branchId, id, replaceOrderFromRealtime, buildRealtimeUpdateMessage, loadPendingOrderTabs]);

  usePurchaseEvents(
    branchId,
    (event) => {
      if (!event || !PURCHASE_ORDER_REALTIME_EVENTS.has(event.type)) return;
      if (String(event.purchaseOrderId || '') !== String(id || '')) return;
      if (isOwnRealtimeEvent(event)) return;
      if (externalRefreshTimerRef.current) {
        clearTimeout(externalRefreshTimerRef.current);
      }
      externalRefreshTimerRef.current = setTimeout(() => {
        externalRefreshTimerRef.current = null;
        refreshOrderFromRealtime(event);
      }, 250);
    },
    { enabled: Boolean(branchId && id) }
  );

  const updateDraft = (itemId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        ...patch,
      },
    }));
  };

  const handleToggleOrderTabStatus = (status) => {
    const normalizedStatus = getCanonicalPurchaseOrderStatus(status);
    if (normalizedStatus === PENDING_ORDER_TAB_STATUS) return;
    setTabStatusFilters((prev) => {
      const normalized = normalizeOrderTabStatusFilters(prev);
      if (normalized.includes(normalizedStatus)) {
        return normalized.filter((entry) => entry !== normalizedStatus);
      }
      return normalizeOrderTabStatusFilters([...normalized, normalizedStatus]);
    });
  };

  const handlePinOrderTab = (orderData, { navigateTo = false } = {}) => {
    if (!orderData?.id) return;
    setPinnedOrderIds((prev) => {
      const next = new Set(prev);
      next.add(getOrderTabId(orderData.id));
      return Array.from(next);
    });
    setPendingOrderTabs((prev) => sortPendingOrderTabs([
      ...prev.filter((entry) => Number(entry.id) !== Number(orderData.id)),
      { ...orderData, isPinnedTab: true },
    ]));
    if (navigateTo) {
      setShowTabFilters(false);
      navigate(`/branches/${branchId}/purchase-orders/${orderData.id}`);
    }
  };

  const handleCloseSpecificOrderTab = (tab) => {
    const orderId = getOrderTabId(tab?.id);
    if (!orderId) return;
    setPinnedOrderIds((prev) => prev.filter((entry) => entry !== orderId));
    setPendingOrderTabs((prev) => sortPendingOrderTabs(
      prev
        .map((entry) => (
          getOrderTabId(entry.id) === orderId
            ? { ...entry, isPinnedTab: false }
            : entry
        ))
        .filter((entry) => shouldShowOrderInTabs({
          order: entry,
          statusFilters: activeOrderTabStatusFilters,
          pinnedOrderIds: pinnedOrderIds.filter((entryId) => entryId !== orderId),
        }))
    ));
  };

  const markDraftForSave = (item, draft) => {
    if (!item?.id) return;
    clearAutoSaveTimer(item.id);
    if (!isDraftReadyToSave(item, draft)) {
      setItemSaveState(item.id, {
        status: 'waiting',
        message: draft.action === 'remove' ? 'Selecciona un motivo.' : 'Completa cantidad y motivo.',
      });
      return;
    }
    const signature = buildItemSaveSignature(item, draft);
    if (requireItemConfirmation && isAcceptWithoutAdjustment(item, draft)) {
      const status = String(item.confirmationStatus || '').trim().toLowerCase();
      setItemSaveState(item.id, {
        status: status === 'confirmed' ? 'confirmed' : 'idle',
        message: '',
      });
      return;
    }
    if (signature === lastSavedSignaturesRef.current[item.id]) {
      const status = String(item.confirmationStatus || '').trim().toLowerCase();
      setItemSaveState(item.id, {
        status: status === 'confirmed' ? 'confirmed' : status === 'saved' ? 'saved' : 'idle',
        message: status === 'saved' && requireItemConfirmation ? 'Cambios guardados. Falta confirmar.' : '',
      });
      return;
    }
    setItemSaveState(item.id, { status: 'pending', message: 'Cambio sin guardar.' });
  };

  const handleStatusChange = async (nextStatus, sourceOrder = order) => {
    if (!nextStatus || !sourceOrder || nextStatus === sourceOrder.status) return;
    markLocalPurchaseOrderMutation();
    setSavingStatus(true);
    try {
      const response = await updatePurchaseOrderStatus(branchId, sourceOrder.id, nextStatus);
      applyOrderResponse(response);
      notify('Estado actualizado.');
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo actualizar el estado.', { type: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleItemDiscountChange = async (itemId, _priceIndex, discount) => {
    if (!order?.id || !itemId || discountSaving) return;
    markLocalPurchaseOrderMutation();
    setDiscountSaving(true);
    try {
      const response = await updatePurchaseOrderItemDiscount(branchId, order.id, itemId, { discount });
      applyOrderResponse(response);
      notify('Descuento de item actualizado.');
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo actualizar el descuento del item.', { type: 'error' });
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleOrderDiscountApply = async (amount, percentage) => {
    if (!order?.id || discountSaving) return;
    markLocalPurchaseOrderMutation();
    setDiscountSaving(true);
    try {
      const response = await updatePurchaseOrderDiscount(branchId, order.id, {
        amount,
        percentage,
      });
      applyOrderResponse(response);
      notify('Descuento total actualizado.');
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo actualizar el descuento total.', { type: 'error' });
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleAllItemsDiscountApply = async ({ discount = null, percentage = null }) => {
    if (!order?.id || discountSaving) return;
    markLocalPurchaseOrderMutation();
    setDiscountSaving(true);
    try {
      const response = await applyPurchaseOrderItemsDiscount(branchId, order.id, {
        discount,
        percentage,
      });
      applyOrderResponse(response);
      notify('Descuento aplicado a todos los items.');
      setShowAllItemsDiscountModal(false);
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo aplicar el descuento a los items.', { type: 'error' });
    } finally {
      setDiscountSaving(false);
    }
  };

  const openAddItemsModal = async () => {
    if (!order?.id) return;
    setShowAddItemsModal(true);
    setAddItemsLoading(true);
    setAddItemsError('');
    try {
      const response = await getPurchaseOrderCatalog(branchId, order.id);
      setAddItemsCatalog(Array.isArray(response?.products) ? response.products : []);
    } catch (err) {
      console.error(err);
      setAddItemsError(err?.response?.data?.message || 'No se pudo cargar el catalogo.');
      setAddItemsCatalog([]);
    } finally {
      setAddItemsLoading(false);
    }
  };

  const handleAddOrderItems = async ({ items, resetEditor }) => {
    if (!order?.id) return;
    markLocalPurchaseOrderMutation();
    const response = await addPurchaseOrderItems(branchId, order.id, items);
    applyOrderResponse(response);
    resetEditor();
    setShowAddItemsModal(false);
    notify('Productos agregados a la orden.');
  };

  const handleConvertToSale = async (sourceOrder = order) => {
    if (!sourceOrder?.id || convertingSale) return;
    if (sourceOrder.purchaseId) {
      navigate(`/branches/${branchId}/purchases/${sourceOrder.purchaseId}/receipt?fromPurchaseOrder=${sourceOrder.id}&offerDeliveryGuide=1`);
      return;
    }
    markLocalPurchaseOrderMutation();
    setConvertingSale(true);
    try {
      const response = await convertPurchaseOrderToSale(branchId, sourceOrder.id);
      if (response?.purchaseOrder) {
        applyOrderResponse(response);
      }
      const purchaseId = response?.purchase?.id || response?.purchaseId || response?.purchaseOrder?.purchaseId;
      if (!purchaseId) {
        notify('La venta fue creada, pero no se pudo abrir el recibo.', { type: 'error' });
        return;
      }
      notify(response?.alreadyConverted ? 'La orden ya tenia una venta asociada.' : 'Venta generada desde la orden.');
      navigate(`/branches/${branchId}/purchases/${purchaseId}/receipt?fromPurchaseOrder=${sourceOrder.id}&offerDeliveryGuide=1`);
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo generar la venta.', { type: 'error' });
    } finally {
      setConvertingSale(false);
    }
  };

  const maybePromptSaleConversion = async (previousAllConfirmed, nextOrder) => {
    if (
      previousAllConfirmed
      || !nextOrder?.allItemsConfirmed
      || nextOrder.status !== 'confirmed'
      || nextOrder.purchaseId
      || !canCreateSale
    ) return;
    const approved = await confirm({
      title: 'Orden confirmada',
      message: 'Todos los productos fueron confirmados. ¿Generar la venta y abrir el recibo?',
      confirmLabel: 'Generar venta',
      cancelLabel: 'Mantener orden',
      tone: 'warning',
    });
    if (approved) {
      await handleConvertToSale(nextOrder);
    }
  };

  const buildItemPayload = (draft) => {
    const action = draft.action || 'accept';
    const previousAllConfirmed = Boolean(order?.allItemsConfirmed);
    const payload = { action };
    if (action === 'reduce' || action === 'increase') {
      payload.confirmedQuantity = Number(draft.confirmedQuantity);
      payload.reasonId = draft.reasonId;
    }
    if (action === 'remove') {
      payload.reasonId = draft.reasonId;
    }
    return { payload, previousAllConfirmed };
  };

  const performSaveItem = async (item, draftOverride = null, { auto = false, force = false } = {}) => {
    if (!order?.id || !item?.id) return;
    const draft = draftOverride || drafts[item.id] || buildDefaultDraft(item);
    if (!isDraftReadyToSave(item, draft)) {
      setItemSaveState(item.id, {
        status: 'waiting',
        message: draft.action === 'remove' ? 'Selecciona un motivo.' : 'Completa cantidad y motivo.',
      });
      return;
    }

    const signature = buildItemSaveSignature(item, draft);
    if (!force && lastSavedSignaturesRef.current[item.id] === signature) {
      const status = String(item.confirmationStatus || '').trim().toLowerCase();
      setItemSaveState(item.id, {
        status: status === 'confirmed' ? 'confirmed' : status === 'saved' ? 'saved' : 'idle',
        message: status === 'saved' && requireItemConfirmation
          ? 'Cambios guardados. Falta confirmar.'
          : 'Sin cambios pendientes.',
      });
      return;
    }
    if (inFlightSignaturesRef.current[item.id]) {
      queuedDraftsRef.current[item.id] = draft;
      setItemSaveState(item.id, { status: 'pending', message: 'Cambio pendiente de guardar.' });
      return;
    }

    clearAutoSaveTimer(item.id);
    inFlightSignaturesRef.current[item.id] = signature;
    const { payload, previousAllConfirmed } = buildItemPayload(draft);
    const saveConfirmsItem = !requireItemConfirmation || draft.action === 'remove';
    const saveRequest = saveConfirmsItem
      ? updatePurchaseOrderItemConfirmation
      : updatePurchaseOrderItemAdjustment;
    markLocalPurchaseOrderMutation();
    setItemSaveState(item.id, { status: 'saving', message: auto ? 'Guardando automaticamente...' : 'Guardando...' });
    try {
      const response = await saveRequest(branchId, order.id, item.id, payload);
      const nextOrder = applyOrderResponse(response);
      lastSavedSignaturesRef.current[item.id] = signature;
      const confirmedMessage = draft.action === 'remove'
        ? (auto ? 'Producto quitado automaticamente.' : 'Producto quitado.')
        : (auto ? 'Guardado y confirmado automaticamente.' : 'Guardado y confirmado.');
      setItemSaveState(item.id, {
        status: saveConfirmsItem ? 'confirmed' : 'saved',
        message: saveConfirmsItem
          ? confirmedMessage
          : (auto ? 'Cambios guardados. Falta confirmar.' : 'Cambios guardados. Falta confirmar.'),
        savedAt: Date.now(),
      });
      if (!auto) notify(saveConfirmsItem ? (draft.action === 'remove' ? 'Producto quitado.' : 'Item confirmado.') : 'Cambios del item guardados.');
      if (saveConfirmsItem) {
        await maybePromptSaleConversion(previousAllConfirmed, nextOrder);
      }
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.message || 'No se pudo guardar el item.';
      setItemSaveState(item.id, { status: 'error', message });
      if (!auto) notify(message, { type: 'error' });
    } finally {
      delete inFlightSignaturesRef.current[item.id];
      const queuedDraft = queuedDraftsRef.current[item.id];
      delete queuedDraftsRef.current[item.id];
      if (queuedDraft && buildItemSaveSignature(item, queuedDraft) !== lastSavedSignaturesRef.current[item.id]) {
        autoSaveTimersRef.current[item.id] = setTimeout(() => {
          performSaveItem(item, queuedDraft, { auto: true });
        }, 0);
      }
    }
  };

  const scheduleAutoSaveItem = (item, draft, { immediate = false } = {}) => {
    if (!item?.id) return;
    clearAutoSaveTimer(item.id);
    if (!autoSaveItemChanges) {
      markDraftForSave(item, draft);
      return;
    }
    if (!isDraftReadyToSave(item, draft)) {
      setItemSaveState(item.id, {
        status: 'waiting',
        message: draft.action === 'remove' ? 'Selecciona un motivo.' : 'Completa cantidad y motivo.',
      });
      return;
    }
    const signature = buildItemSaveSignature(item, draft);
    if (requireItemConfirmation && isAcceptWithoutAdjustment(item, draft)) {
      setItemSaveState(item.id, {
        status: String(item.confirmationStatus || '').trim().toLowerCase() === 'confirmed' ? 'confirmed' : 'idle',
        message: '',
      });
      return;
    }
    if (signature === lastSavedSignaturesRef.current[item.id]) {
      const status = String(item.confirmationStatus || '').trim().toLowerCase();
      setItemSaveState(item.id, {
        status: status === 'confirmed' ? 'confirmed' : status === 'saved' ? 'saved' : 'idle',
        message: status === 'saved' && requireItemConfirmation
          ? 'Cambios guardados. Falta confirmar.'
          : 'Sin cambios pendientes.',
      });
      return;
    }
    setItemSaveState(item.id, { status: 'pending', message: immediate ? 'Guardando...' : 'Guardado automatico pendiente.' });
    const delay = immediate ? 0 : 700;
    autoSaveTimersRef.current[item.id] = setTimeout(() => {
      performSaveItem(item, draft, { auto: true });
    }, delay);
  };

  const handleSaveItem = async (item) => {
    const draft = drafts[item.id] || buildDefaultDraft(item);
    const normalizedDraft = {
      ...draft,
      action: resolveQuantityAction(item, draft.confirmedQuantity),
      confirmedQuantity: formatQuantityInput(draft.confirmedQuantity),
    };
    if (normalizedDraft.confirmedQuantity !== draft.confirmedQuantity || normalizedDraft.action !== draft.action) {
      updateDraft(item.id, {
        action: normalizedDraft.action,
        confirmedQuantity: normalizedDraft.confirmedQuantity,
        reasonId: normalizedDraft.action === 'accept' ? '' : normalizedDraft.reasonId,
      });
    }
    if (requireItemConfirmation && isAcceptWithoutAdjustment(item, normalizedDraft)) {
      await handleConfirmProcessedItem(item, normalizedDraft);
      return;
    }
    const forceRemoveConfirmation = normalizedDraft.action === 'remove'
      && String(item.confirmationStatus || '').trim().toLowerCase() !== 'confirmed';
    await performSaveItem(item, normalizedDraft, { auto: false, force: forceRemoveConfirmation });
  };

  const handleConfirmProcessedItem = async (item, draftOverride = null) => {
    if (!order?.id || !item?.id) return;
    const draft = draftOverride || drafts[item.id] || buildDefaultDraft(item);
    const normalizedDraft = {
      ...draft,
      action: resolveQuantityAction(item, draft.confirmedQuantity),
      confirmedQuantity: formatQuantityInput(draft.confirmedQuantity),
    };
    if (!isDraftReadyToSave(item, normalizedDraft)) {
      setItemSaveState(item.id, {
        status: 'waiting',
        message: normalizedDraft.action === 'remove' ? 'Selecciona un motivo.' : 'Completa cantidad y motivo.',
      });
      return;
    }
    const signature = buildItemSaveSignature(item, normalizedDraft);
    const canConfirmDirectly = requireItemConfirmation && isAcceptWithoutAdjustment(item, normalizedDraft);
    if (!canConfirmDirectly && signature !== lastSavedSignaturesRef.current[item.id]) {
      setItemSaveState(item.id, { status: 'pending', message: 'Guarda los cambios antes de confirmar.' });
      return;
    }
    if (inFlightSignaturesRef.current[item.id]) {
      setItemSaveState(item.id, { status: 'pending', message: 'Espera a que termine el guardado.' });
      return;
    }

    clearAutoSaveTimer(item.id);
    delete queuedDraftsRef.current[item.id];
    inFlightSignaturesRef.current[item.id] = signature;
    const { payload, previousAllConfirmed } = buildItemPayload(normalizedDraft);
    markLocalPurchaseOrderMutation();
    setItemSaveState(item.id, { status: 'confirming', message: 'Confirmando item procesado...' });
    try {
      const response = await updatePurchaseOrderItemConfirmation(branchId, order.id, item.id, payload);
      const nextOrder = applyOrderResponse(response);
      lastSavedSignaturesRef.current[item.id] = signature;
      setItemSaveState(item.id, {
        status: 'confirmed',
        message: 'Item procesado confirmado.',
        savedAt: Date.now(),
      });
      notify('Item procesado confirmado.');
      await maybePromptSaleConversion(previousAllConfirmed, nextOrder);
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.message || 'No se pudo confirmar el item.';
      setItemSaveState(item.id, { status: 'error', message });
      notify(message, { type: 'error' });
    } finally {
      delete inFlightSignaturesRef.current[item.id];
    }
  };

  const updateQuantityDraft = (item, rawQuantity, { immediate = false } = {}) => {
    const draft = drafts[item.id] || buildDefaultDraft(item);
    const nextQuantity = sanitizeQuantityInput(rawQuantity);
    const nextAction = resolveQuantityAction(item, nextQuantity);
    const nextDraft = {
      ...draft,
      action: nextAction,
      confirmedQuantity: nextQuantity,
      reasonId: nextAction === 'accept' || nextAction !== draft.action ? '' : draft.reasonId,
    };
    updateDraft(item.id, {
      action: nextDraft.action,
      confirmedQuantity: nextDraft.confirmedQuantity,
      reasonId: nextDraft.reasonId,
    });
    scheduleAutoSaveItem(item, nextDraft, { immediate: immediate || nextAction === 'accept' });
  };

  const adjustQuantityDraft = (item, delta) => {
    const draft = drafts[item.id] || buildDefaultDraft(item);
    const current = Number(draft.confirmedQuantity);
    const baseQuantity = Number.isFinite(current) ? current : Number(item.requestedQuantity || 0);
    const nextQuantity = Math.max(0, baseQuantity + delta);
    updateQuantityDraft(item, formatQuantityInput(nextQuantity), { immediate: nextQuantity === Number(item.requestedQuantity || 0) });
  };

  const openNumericPad = (item) => {
    const draft = drafts[item.id] || buildDefaultDraft(item);
    setNumericPadItemId(item.id);
    setNumericPadValue(formatQuantityInput(draft.confirmedQuantity));
  };

  const closeNumericPad = () => {
    setNumericPadItemId(null);
    setNumericPadValue('');
  };

  const applyNumericPadValue = () => {
    const item = items.find((entry) => Number(entry.id) === Number(numericPadItemId));
    if (!item) {
      closeNumericPad();
      return;
    }
    updateQuantityDraft(item, formatQuantityInput(numericPadValue), { immediate: true });
    closeNumericPad();
  };

  const handleNumericPadKey = (key) => {
    if (key === 'clear') {
      setNumericPadValue('');
      return;
    }
    if (key === 'backspace') {
      setNumericPadValue((prev) => prev.slice(0, -1));
      return;
    }
    if (key === '.') {
      setNumericPadValue((prev) => (prev.includes('.') ? prev : `${prev || '0'}.`));
      return;
    }
    setNumericPadValue((prev) => sanitizeQuantityInput(`${prev}${key}`));
  };

  const openAllItemsDiscountModal = () => {
    setAllItemsDiscountMode('amount');
    setAllItemsDiscountRaw('0');
    setShowAllItemsDiscountModal(true);
  };

  const handleAllItemsDiscountInput = (value) => {
    const cleaned = String(value ?? '').replace(/[^\d]/g, '').replace(/^0+(?!$)/, '');
    setAllItemsDiscountRaw(cleaned || '0');
  };

  const handleAllItemsDiscountPad = (key) => {
    if (key === 'C') {
      setAllItemsDiscountRaw('0');
      return;
    }
    if (key === 'backspace') {
      setAllItemsDiscountRaw((prev) => prev.slice(0, -1) || '0');
      return;
    }
    if (key === '00') {
      setAllItemsDiscountRaw((prev) => (prev === '0' ? '0' : `${prev}00`));
      return;
    }
    if (/\d/.test(String(key))) {
      setAllItemsDiscountRaw((prev) => (prev === '0' ? String(key) : `${prev}${key}`));
    }
  };

  const renderReasonSelect = (item, group) => {
    const options = reasons[group] || [];
    const draft = drafts[item.id] || buildDefaultDraft(item);
    return (
      <select
        value={draft.reasonId || ''}
        onChange={(event) => {
          const nextDraft = { ...draft, reasonId: event.target.value };
          updateDraft(item.id, { reasonId: event.target.value });
          scheduleAutoSaveItem(item, nextDraft, { immediate: true });
        }}
        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm"
      >
        <option value="">Selecciona motivo</option>
        {options.map((reason) => (
          <option key={reason.id} value={reason.id}>
            {reason.label}
          </option>
        ))}
      </select>
    );
  };

  const renderItemSaveIndicator = (saveState) => {
    if (!saveState || saveState.status === 'idle' || !saveState.message) return null;
    if (saveState.message === 'Sin cambios pendientes.') return null;
    const toneClass = saveState.status === 'error'
      ? 'text-red-700'
      : saveState.status === 'confirmed'
        ? 'text-emerald-700'
        : saveState.status === 'saved'
          ? 'text-amber-700'
        : saveState.status === 'waiting'
          ? 'text-amber-700'
          : 'text-blue-700';
    return (
      <p className={`text-xs ${toneClass}`}>
        {saveState.message}
      </p>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <InlineLoading label="Cargando orden" />
      </div>
    );
  }

  if (error) return <p className="p-6 text-sm text-red-600">{error}</p>;
  if (!order) return <p className="p-6 text-sm text-gray-600">No se encontro la orden.</p>;

  const history = [...(order.History || [])].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
  const latestHistory = history.slice(0, 3);
  const itemCount = Number(order.itemCount || items.length || 0);
  const confirmedItemCount = Number(order.confirmedItemCount || 0);
  const progressPercent = itemCount > 0
    ? Math.min(100, Math.round((confirmedItemCount / itemCount) * 100))
    : 0;
  const hasGeneratedSale = Boolean(order.purchaseId);
  const canAddMissingProduct = Boolean(
    !hasGeneratedSale
    && (isPurchaseOrderPreparingStatus(order.status) || order.status === 'confirmed')
  );
  const canGenerateSale = Boolean(
    canCreateSale
    && order.allItemsConfirmed
    && !hasGeneratedSale
    && order.status !== 'cancelled'
  );
  const numericPadItem = items.find((entry) => Number(entry.id) === Number(numericPadItemId));
  const discountModalPrice = discountItem ? {
    id: discountItem.varietyPriceId || discountItem.id,
    price: Number(discountItem.price || 0),
    metricUnit: discountItem.metricUnit,
    priceImage: getItemImage(discountItem),
  } : null;
  const discountModalVariety = discountItem && discountModalPrice ? {
    id: discountItem.id,
    varietyName: discountItem.varietyName || discountItem.productName || 'Producto',
    varietyImage: getItemImage(discountItem),
    VarietyPrices: [discountModalPrice],
  } : null;
  const discountMatrix = discountItem ? {
    [discountItem.id]: [getUnitDiscount(discountItem)],
  } : {};

  const renderRecentActivity = () => {
    if (!latestHistory.length || activeTab !== 'review') return null;
    return (
      <section className="rounded-xl border border-blue-100 bg-blue-50/70 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
            <FiClock />
            Actividad reciente
          </div>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className="text-xs font-semibold text-blue-700 hover:underline"
          >
            Ver historial
          </button>
        </div>
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          {latestHistory.map((entry) => (
            <div
              key={entry.id}
              className="min-w-[190px] rounded-lg border border-blue-100 bg-white px-3 py-2"
            >
              <p className="truncate text-xs font-semibold text-gray-900">
                {historyEventLabel(entry.eventType)}
              </p>
              <p className="mt-0.5 truncate text-[11px] text-gray-500">
                {historyActorName(entry)} · {formatDateTime(entry.createdAt)}
              </p>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderItemCard = (item) => {
    const draft = drafts[item.id] || buildDefaultDraft(item);
    const action = resolveQuantityAction(item, draft.confirmedQuantity);
    const saveState = itemSaveStates[item.id] || { status: 'idle', message: '' };
    const isSaving = saveState.status === 'saving';
    const isConfirming = saveState.status === 'confirming';
    const currentSignature = buildItemSaveSignature(item, draft);
    const savedSignature = lastSavedSignaturesRef.current[item.id];
    const confirmationStatus = String(item.confirmationStatus || '').trim().toLowerCase();
    const isServerConfirmed = confirmationStatus === 'confirmed';
    const isServerSaved = confirmationStatus === 'saved';
    const canConfirmAcceptWithoutSave = requireItemConfirmation && isAcceptWithoutAdjustment(item, draft);
    const hasUnsavedChanges = currentSignature !== savedSignature && !canConfirmAcceptWithoutSave;
    const isConfirmed = isServerConfirmed && !hasUnsavedChanges;
    const isSavedAwaitingConfirmation = requireItemConfirmation && action !== 'remove' && isServerSaved && !hasUnsavedChanges;
    const confirmedAction = isConfirmed ? normalizePurchaseOrderAction(item.confirmationAction) : '';
    const showAdjustmentControls = action !== 'accept';
    const draftReady = isDraftReadyToSave(item, draft);
    const savedRemoveAwaitingConfirmation = action === 'remove' && isServerSaved && !hasUnsavedChanges;
    const saveDisabled = isSaving
      || isConfirming
      || !draftReady
      || isConfirmed
      || (!hasUnsavedChanges && saveState.status !== 'error' && !savedRemoveAwaitingConfirmation);
    const confirmDisabled = isSaving
      || isConfirming
      || !draftReady
      || isConfirmed
      || (!canConfirmAcceptWithoutSave && (hasUnsavedChanges || !isServerSaved));
    const statusBadgeLabel = hasUnsavedChanges
      ? 'Sin guardar'
      : canConfirmAcceptWithoutSave && !isConfirmed
        ? 'Pendiente'
      : getPurchaseOrderItemActionLabel(item);
    const statusBadgeToneClass = hasUnsavedChanges
      ? 'bg-blue-100 text-blue-700'
      : getPurchaseOrderItemActionTone(item);
    const unitDiscount = getUnitDiscount(item);
    const discountedUnitPrice = getDiscountedUnitPrice(item);
    const cardToneClass = action === 'remove' || confirmedAction === 'remove'
      ? 'border-red-300 bg-red-50/80'
      : isConfirmed
        ? 'border-emerald-300 bg-emerald-50/80'
        : isSavedAwaitingConfirmation
          ? 'border-amber-300 bg-amber-50/70'
        : 'border-gray-200 bg-white';
    const panelToneClass = action === 'remove' || confirmedAction === 'remove'
      ? 'border-red-200 bg-white/80'
      : isConfirmed
        ? 'border-emerald-200 bg-white/80'
        : isSavedAwaitingConfirmation
          ? 'border-amber-200 bg-white/80'
        : 'border-gray-200 bg-gray-50';
    const singleReviewActionDisabled = action === 'remove' ? saveDisabled : confirmDisabled;
    const singleReviewActionLabel = action === 'remove'
      ? isSaving || isConfirming
        ? 'Quitando...'
        : isConfirmed
          ? 'Confirmado'
          : saveState.status === 'error'
            ? 'Reintentar'
            : 'Quitar producto'
      : isConfirming
        ? 'Confirmando...'
        : isConfirmed
          ? 'Confirmado'
          : 'Confirmar';
    return (
      <article
        key={item.id}
        className={`rounded-lg border p-3 shadow-sm sm:rounded-xl sm:p-4 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:gap-4 ${cardToneClass}`}
      >
        <div className="flex gap-2 sm:gap-3 lg:min-w-0">
          <img
            src={getItemImage(item)}
            alt={item.varietyName || item.productName || 'Producto'}
            className="h-14 w-14 flex-shrink-0 rounded-lg border border-gray-200 bg-white object-cover sm:h-20 sm:w-20 lg:h-24 lg:w-24"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold leading-tight text-gray-900">
                  {item.productName || 'Producto'}
                </h2>
                <p className="truncate text-xs text-gray-600">
                  {item.varietyName || 'Variedad'} · {item.priceDescription || item.metricUnit || 'Formato'} · {formatMoney(discountedUnitPrice)} c/u
                </p>
              </div>
              <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeToneClass}`}>
                {statusBadgeLabel}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs sm:hidden">
              <span className="rounded-full bg-sky-50 px-2 py-1 text-sky-800">
                Sol <strong>{formatQuantity(item.requestedQuantity)}</strong>
              </span>
              <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-800">
                Act <strong>{formatQuantity(item.currentQuantity)}</strong>
              </span>
              <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-800">
                Total <strong>{formatMoney(item.currentSubtotal)}</strong>
              </span>
              {unitDiscount > 0 && (
                <span className="rounded-full bg-red-50 px-2 py-1 text-red-700">
                  Desc <strong>-{formatMoney(unitDiscount)}</strong>
                </span>
              )}
            </div>

            <div className="mt-3 hidden grid-cols-4 gap-2 text-sm sm:grid">
              <div className="rounded-lg bg-gray-50 px-2 py-2">
                <p className="text-[11px] uppercase text-gray-500">Solicitado</p>
                <p className="font-semibold text-gray-900">{formatQuantity(item.requestedQuantity)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-2">
                <p className="text-[11px] uppercase text-gray-500">Actual</p>
                <p className="font-semibold text-gray-900">{formatQuantity(item.currentQuantity)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-2">
                <p className="text-[11px] uppercase text-gray-500">Precio</p>
                <p className="font-semibold text-gray-900">{formatMoney(discountedUnitPrice)}</p>
                {unitDiscount > 0 && (
                  <p className="text-[11px] text-red-600">-{formatMoney(unitDiscount)} c/u</p>
                )}
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-2">
                <p className="text-[11px] uppercase text-gray-500">Subtotal</p>
                <p className="font-semibold text-gray-900">{formatMoney(item.currentSubtotal)}</p>
              </div>
            </div>

            {item.adjustmentReasonLabel && (
              <p className="mt-1.5 truncate rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800 sm:mt-2 sm:rounded-lg sm:py-1.5">
                Motivo: {item.adjustmentReasonLabel}
              </p>
            )}
            {unitDiscount > 0 && (
              <p className="mt-1.5 truncate rounded-md bg-red-50 px-2 py-1 text-xs text-red-700 sm:mt-2 sm:rounded-lg sm:py-1.5">
                Descuento: -{formatMoney(unitDiscount)} por unidad
              </p>
            )}
          </div>
        </div>

        <div className={`mt-2.5 border-t border-gray-100 pt-2.5 sm:mt-4 sm:rounded-xl sm:border sm:p-3 lg:mt-0 lg:flex lg:min-h-full lg:flex-col lg:justify-between ${panelToneClass}`}>
          <div className="grid grid-cols-[36px_minmax(0,1fr)_36px_36px_36px] gap-1.5 sm:grid-cols-[42px_minmax(0,220px)_42px_42px_42px] sm:gap-2 lg:grid-cols-[42px_minmax(0,1fr)_42px_42px_42px]">
            <button
              type="button"
              onClick={() => adjustQuantityDraft(item, -1)}
              className="flex min-h-10 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-400"
              aria-label="Disminuir cantidad"
            >
              <FiMinus />
            </button>
            <input
              type="text"
              inputMode="decimal"
              value={draft.confirmedQuantity}
              onChange={(event) => updateQuantityDraft(item, event.target.value)}
              onBlur={(event) => {
                const formatted = formatQuantityInput(event.target.value);
                updateQuantityDraft(item, formatted, { immediate: true });
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-center text-base font-semibold text-gray-900"
              aria-label="Cantidad confirmada"
            />
            <button
              type="button"
              onClick={() => adjustQuantityDraft(item, 1)}
              className="flex min-h-10 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:border-blue-400"
              aria-label="Aumentar cantidad"
            >
              <FiPlus />
            </button>
            <button
              type="button"
              onClick={() => openNumericPad(item)}
              className="flex min-h-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-400"
              aria-label="Abrir teclado numerico"
            >
              <FaCalculator />
            </button>
            <button
              type="button"
              onClick={() => setDiscountItem(item)}
              disabled={discountSaving || Boolean(order.purchaseId)}
              className={`flex min-h-10 items-center justify-center rounded-lg border bg-white hover:border-red-300 disabled:opacity-60 ${
                unitDiscount > 0
                  ? 'border-red-300 text-red-700'
                  : 'border-gray-200 text-gray-700'
              }`}
              aria-label={unitDiscount > 0 ? 'Editar descuento de item' : 'Agregar descuento de item'}
              title={unitDiscount > 0 ? 'Editar descuento' : 'Descuento'}
            >
              <FiTag />
            </button>
          </div>

          {showAdjustmentControls && (
            <div className="mt-2 sm:mt-3">
              {renderReasonSelect(item, action)}
            </div>
          )}
          {requireItemConfirmation ? (
            showAdjustmentControls && action !== 'remove' ? (
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => handleSaveItem(item)}
                  disabled={saveDisabled}
                  className="min-h-9 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-black disabled:opacity-60 sm:min-h-[42px] sm:px-4 sm:text-sm"
                >
                  {isSaving ? 'Guardando...' : saveState.status === 'error' ? 'Reintentar' : 'Guardar cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirmProcessedItem(item)}
                  disabled={confirmDisabled}
                  className={`min-h-9 rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-70 sm:min-h-[42px] sm:px-4 sm:text-sm ${
                    isConfirmed
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-800 border border-gray-300 hover:border-gray-500'
                  }`}
                >
                  {isConfirming ? 'Confirmando...' : isConfirmed ? 'Confirmado' : 'Confirmar procesado'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => (action === 'remove' ? handleSaveItem(item) : handleConfirmProcessedItem(item))}
                disabled={singleReviewActionDisabled}
                className={`mt-2 min-h-9 w-full rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-70 sm:px-4 sm:text-sm lg:w-full ${
                  isConfirmed
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-900 text-white hover:bg-black'
                }`}
              >
                {singleReviewActionLabel}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => handleSaveItem(item)}
              disabled={isSaving || isConfirming}
              className="mt-2 min-h-9 w-full rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-black disabled:opacity-60 sm:w-auto sm:px-4 sm:text-sm lg:w-full"
            >
              {isSaving ? 'Guardando...' : saveState.status === 'error' ? 'Reintentar' : showAdjustmentControls ? 'Guardar' : 'Confirmar'}
            </button>
          )}
          <div className="mt-1 sm:mt-2">
            {renderItemSaveIndicator(saveState)}
          </div>
        </div>
      </article>
    );
  };

  const renderHistoryTab = () => (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900">Historial</h2>
          <p className="text-sm text-gray-500">Cambios de estado e items de esta orden.</p>
        </div>
      </div>
      {history.length === 0 ? (
        <p className="mt-4 text-sm text-gray-600">Sin movimientos registrados.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {history.map((entry) => (
            <div key={entry.id} className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{historyEventLabel(entry.eventType)}</p>
                  <p className="mt-1 text-xs text-gray-600">{historyActorName(entry)}</p>
                </div>
                <p className="shrink-0 text-right text-xs text-gray-500">{formatDateTime(entry.createdAt)}</p>
              </div>
              {renderHistoryDetails(entry)}
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const renderNumericPadModal = () => {
    if (!numericPadItem) return null;
    const padKeys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0'];
    return (
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 pt-8"
        onClick={closeNumericPad}
      >
        <div
          className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Teclado numerico"
        >
          <div className="flex items-start gap-3">
            <img
              src={getItemImage(numericPadItem)}
              alt={numericPadItem.varietyName || numericPadItem.productName || 'Producto'}
              className="h-14 w-14 rounded-lg border border-gray-200 object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-gray-900">
                {numericPadItem.productName || 'Producto'}
              </p>
              <p className="truncate text-sm text-gray-600">
                {numericPadItem.varietyName || 'Variedad'}
              </p>
              <p className="text-xs text-gray-500">
                Solicitado: {formatQuantity(numericPadItem.requestedQuantity)}
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-center text-3xl font-semibold text-gray-900">
            {numericPadValue || '0'}
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            {padKeys.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => handleNumericPadKey(key)}
                className="min-h-12 rounded-lg border border-gray-200 bg-white text-lg font-semibold text-gray-900 hover:bg-gray-50"
              >
                {key}
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleNumericPadKey('backspace')}
              className="flex min-h-12 items-center justify-center rounded-lg border border-gray-200 bg-white text-lg font-semibold text-gray-900 hover:bg-gray-50"
              aria-label="Borrar ultimo digito"
            >
              <FiDelete />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleNumericPadKey('clear')}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Limpiar
            </button>
            <button
              type="button"
              onClick={applyNumericPadValue}
              className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black"
            >
              Aplicar
            </button>
          </div>
          <button
            type="button"
            onClick={closeNumericPad}
            className="mt-2 w-full rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  };

  const renderAllItemsDiscountModal = () => {
    if (!showAllItemsDiscountModal) return null;
    const numericValue = parseInt(allItemsDiscountRaw || '0', 10) || 0;
    const percentage = Math.min(Math.max(numericValue, 0), 100);
    const affectedItems = items.filter((item) => Number(item.currentQuantity || item.requestedQuantity || 0) > 0);
    const previewDiscount = affectedItems.reduce((sum, item) => {
      const price = Number(item.price || 0);
      const quantity = Number(item.currentQuantity || item.requestedQuantity || 0);
      const unitDiscount = allItemsDiscountMode === 'percent'
        ? Math.floor(price * (percentage / 100))
        : Math.min(numericValue, price);
      return sum + Math.floor(Math.max(unitDiscount, 0) * Math.max(quantity, 0));
    }, 0);
    const padKeys = [1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '00'];

    return (
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-6"
        onClick={() => setShowAllItemsDiscountModal(false)}
      >
        <div
          className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Descuento para todos los items"
        >
          <div className="flex items-start justify-between border-b border-gray-100 bg-gray-50 px-6 py-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Descuento</p>
              <h2 className="text-lg font-semibold text-gray-900">Todos los items</h2>
              <p className="mt-1 text-xs text-gray-500">Aplica descuento por unidad a cada producto.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAllItemsDiscountModal(false)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
            >
              X
            </button>
          </div>

          <div className="space-y-5 p-6">
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-100 p-1 text-sm font-semibold">
              <button
                type="button"
                onClick={() => setAllItemsDiscountMode('amount')}
                className={`rounded-md px-3 py-2 ${allItemsDiscountMode === 'amount' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
              >
                $ por unidad
              </button>
              <button
                type="button"
                onClick={() => setAllItemsDiscountMode('percent')}
                className={`rounded-md px-3 py-2 ${allItemsDiscountMode === 'percent' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
              >
                %
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Items afectados</div>
                <div className="text-base font-semibold text-gray-900">{affectedItems.length}</div>
              </div>
              <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                <div className="text-xs text-red-600">Desc. estimado</div>
                <div className="text-base font-semibold text-red-700">-{formatMoney(previewDiscount)}</div>
              </div>
            </div>

            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={allItemsDiscountRaw}
                onChange={(event) => handleAllItemsDiscountInput(event.target.value)}
                className="w-full rounded-lg border border-gray-300 p-3 pr-10 text-center text-xl"
              />
              <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">
                {allItemsDiscountMode === 'percent' ? '%' : '$'}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {padKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleAllItemsDiscountPad(key)}
                  className="rounded-lg bg-gray-100 py-3 text-lg hover:bg-gray-200"
                >
                  {key}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleAllItemsDiscountPad('backspace')}
                className="flex items-center justify-center rounded-lg bg-gray-100 py-3 text-lg hover:bg-gray-200"
                aria-label="Borrar ultimo digito"
              >
                <FiDelete />
              </button>
              <button
                type="button"
                onClick={() => handleAllItemsDiscountApply(
                  allItemsDiscountMode === 'percent'
                    ? { percentage }
                    : { discount: numericValue }
                )}
                disabled={discountSaving}
                className="col-span-2 rounded-lg bg-blue-600 py-3 text-lg font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {discountSaving ? 'Aplicando...' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddItemsModal = () => {
    if (!showAddItemsModal) return null;
    return (
      <div
        className="fixed inset-0 z-[1100] flex items-start justify-center overflow-y-auto bg-black/60 px-3 py-4 sm:px-4"
        onClick={() => setShowAddItemsModal(false)}
      >
        <div
          className="flex h-[calc(100dvh-2rem)] w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Agregar producto faltante"
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-5">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Orden #{order.id}</p>
              <h2 className="text-lg font-semibold text-gray-900">Agregar producto faltante</h2>
              <p className="text-sm text-gray-600">Catalogo mayorista configurado para este cliente.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAddItemsModal(false)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
              aria-label="Cerrar"
            >
              <FiX />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 overflow-hidden p-4">
            {addItemsLoading ? (
              <div className="flex h-full items-center justify-center">
                <InlineLoading label="Cargando catalogo" />
              </div>
            ) : addItemsError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {addItemsError}
              </div>
            ) : (
              <PurchaseOrderCatalogEditor
                catalog={addItemsCatalog}
                resetKey={`internal-add-${order.id}-${addItemsCatalog.length}`}
                submitLabel="Agregar a la orden"
                cartTitle="Productos a agregar"
                cartDescription="Selecciona productos faltantes para esta orden."
                showNotes={false}
                layout="modal"
                onSubmit={handleAddOrderItems}
                productModalZClassName="z-[1110]"
                varietyModalZClassName="z-[1120]"
                quantityModalZClassName="z-[1130]"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOrderTotalsPanel = () => (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Resumen de la orden</h2>
          <p className="text-sm text-gray-500">Totales generales antes de generar la venta.</p>
        </div>
        {!hasGeneratedSale && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:w-[360px]">
            <button
              type="button"
              onClick={() => setShowOrderDiscountModal(true)}
              disabled={discountSaving}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              <FiPercent />
              Descuento total
            </button>
            <button
              type="button"
              onClick={openAllItemsDiscountModal}
              disabled={discountSaving}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
            >
              <FiTag />
              Descuento todos
            </button>
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase text-gray-500">Subtotal actual</p>
          <p className="font-semibold text-gray-900">{formatMoney(itemsSubtotalBeforeDiscount)}</p>
        </div>
        <div className={`rounded-lg border px-3 py-2 ${
          totalDiscountAmount > 0
            ? 'border-red-100 bg-red-50 text-red-700'
            : 'border-gray-200 bg-gray-50 text-gray-700'
        }`}
        >
          <p className="text-[11px] uppercase opacity-80">Desc. total</p>
          <p className="font-semibold">
            {totalDiscountAmount > 0 ? `-${formatMoney(totalDiscountAmount)}` : formatMoney(0)}
          </p>
          {discountBreakdown.length > 0 && (
            <p className="mt-0.5 text-[11px] opacity-80">{discountBreakdown.join(' · ')}</p>
          )}
        </div>
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-blue-900">
          <p className="text-[11px] uppercase text-blue-700">Total actual</p>
          <p className="font-bold">{formatMoney(computedCurrentTotalAmount)}</p>
        </div>
      </div>
    </section>
  );

  const renderOrderTabFilters = () => {
    const hasExtraFilters = activeOrderTabStatusFilters.length > 1 || pinnedOrderIds.length > 0;
    return (
      <div ref={tabFiltersRef} className="relative">
        <button
          type="button"
          onClick={() => setShowTabFilters((prev) => !prev)}
          title="Filtrar tabs"
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md border ${
            hasExtraFilters
              ? 'border-blue-300 bg-blue-50 text-blue-700'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'
          }`}
          aria-label="Filtrar tabs de ordenes"
        >
          <FiFilter size={14} />
        </button>

        {showTabFilters && (
          <div className="absolute right-0 top-full z-[1000] mt-1 w-80 rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="border-b px-3 py-2">
              <p className="text-xs font-semibold text-gray-800">Tabs de ordenes</p>
              <p className="text-[11px] text-gray-500">Agrega estados o selecciona una orden de la lista.</p>
            </div>

            <div className="space-y-3 p-3">
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase text-gray-500">Estados</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {PURCHASE_ORDER_STATUS_OPTIONS.map((option) => {
                    const checked = activeOrderTabStatusFilters.includes(option.value);
                    const locked = option.value === PENDING_ORDER_TAB_STATUS;
                    return (
                      <label
                        key={option.value}
                        className="flex items-center justify-between gap-3 rounded border border-gray-100 px-2 py-1.5 text-xs text-gray-700"
                      >
                        <span>{option.label}</span>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          onChange={() => handleToggleOrderTabStatus(option.value)}
                          className="h-4 w-4 accent-blue-600 disabled:opacity-50"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase text-gray-500">Todas las ordenes</p>
                  <button
                    type="button"
                    onClick={loadAllOrderOptions}
                    className="text-[11px] font-semibold text-blue-700 hover:underline"
                  >
                    Refrescar
                  </button>
                </div>
                <input
                  type="text"
                  value={orderTabSearch}
                  onChange={(event) => setOrderTabSearch(event.target.value)}
                  placeholder="Buscar por orden, cliente, RUT o estado"
                  className="h-9 w-full rounded border border-gray-300 px-2 text-sm"
                />
                <div className="max-h-64 overflow-y-auto rounded border border-gray-200">
                  {allOrdersLoading ? (
                    <div className="px-3 py-3 text-xs text-gray-500">
                      <InlineLoading label="Cargando ordenes" />
                    </div>
                  ) : allOrdersError ? (
                    <div className="px-3 py-3 text-xs text-red-600">
                      {allOrdersError}
                    </div>
                  ) : filteredAllOrderOptions.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-gray-500">
                      No hay ordenes para mostrar.
                    </div>
                  ) : (
                    filteredAllOrderOptions.map((entry) => {
                      const isPinned = pinnedOrderIds.includes(getOrderTabId(entry.id));
                      const isVisibleInTabs = pendingOrderTabs.some((tab) => Number(tab.id) === Number(entry.id));
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          onClick={() => handlePinOrderTab(entry, { navigateTo: true })}
                          className="flex w-full items-start justify-between gap-2 border-b border-gray-100 px-3 py-2 text-left text-xs hover:bg-gray-50 last:border-b-0"
                        >
                          <span className="min-w-0">
                            <span className="block font-semibold text-gray-900">Orden #{entry.id}</span>
                            <span className="block truncate text-[11px] text-gray-500">
                              {entry.Client?.name || 'Cliente'}{entry.Client?.rut ? ` · ${entry.Client.rut}` : ''}
                            </span>
                            <span className="mt-0.5 block text-[11px] text-gray-500">
                              {formatMoney(entry.currentTotalAmount)} · {formatDateTime(entry.updatedAt)}
                            </span>
                          </span>
                          <span className="flex shrink-0 flex-col items-end gap-1">
                            <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${getPurchaseOrderStatusTone(entry.status)}`}>
                              {getPurchaseOrderStatusLabel(entry.status)}
                            </span>
                            {(isPinned || isVisibleInTabs) && (
                              <span className="text-[10px] font-semibold text-blue-700">
                                {isPinned ? 'Fijada' : 'En tabs'}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {pinnedOrderIds.length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase text-gray-500">Ordenes fijadas</p>
                  <div className="flex flex-wrap gap-1">
                    {pinnedOrderIds.map((orderId) => (
                      <button
                        key={orderId}
                        type="button"
                        onClick={() => handleCloseSpecificOrderTab({ id: orderId })}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        #{orderId}
                        <FiX size={12} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPendingOrderTabs = () => {
    return (
      <DocumentTabs
        tabs={pendingOrderTabs}
        activeId={order.id}
        activeNew={false}
        onSelect={(entry) => navigate(`/branches/${branchId}/purchase-orders/${entry.id}`)}
        onDelete={handleCloseSpecificOrderTab}
        canCreate={false}
        canDelete
        canDeleteTab={(entry) => Boolean(entry?.isPinnedTab)}
        canSelect
        loading={pendingOrdersLoading}
        error={pendingOrdersError}
        scopeReady
        tone="navbar"
        stickyTopClassName="top-0"
        listTitle="Ver todas las ordenes"
        menuTitle="Todas las ordenes"
        emptyMessage="No hay ordenes para mostrar."
        menuTabs={allOrderOptions}
        menuLoading={allOrdersLoading}
        menuError={allOrdersError}
        onListOpen={loadAllOrderOptions}
        onMenuSelect={(entry) => handlePinOrderTab(entry, { navigateTo: true })}
        getTabTitle={(entry) => `Orden #${entry.id}`}
        getTabMeta={buildPendingOrderTabMeta}
        getMenuTabTitle={(entry) => `Orden #${entry.id}`}
        getMenuTabMeta={(entry) => `${getPurchaseOrderStatusLabel(entry.status)} · ${buildPendingOrderTabMeta(entry)}`}
        toolbarActions={renderOrderTabFilters()}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 px-3 py-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        {renderPendingOrderTabs()}
        <section className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(560px,0.95fr)] xl:items-center">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/branches/${branchId}/purchase-orders`)}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-700 hover:bg-blue-50"
                  aria-label="Volver a ordenes"
                  title="Volver a ordenes"
                >
                  <FiArrowLeft />
                </button>
                <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Orden #{order.id}</h1>
                <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${getPurchaseOrderStatusTone(order.status)}`}>
                  {getPurchaseOrderStatusLabel(order.status)}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 sm:text-sm">
                <span>{order.Client?.name || 'Cliente'}</span>
                {order.Client?.rut && <span>RUT: {order.Client.rut}</span>}
                <span>Creada: {formatDateTime(order.createdAt)}</span>
              </div>
              {order.notes && (
                <p className="mt-2 line-clamp-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700 sm:text-sm">
                  {order.notes}
                </p>
              )}
            </div>

            <div className="grid gap-2 md:grid-cols-[180px_minmax(0,1fr)] md:items-center">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs uppercase text-gray-500">Progreso</p>
                  <p className="text-sm font-semibold text-gray-900">{confirmedItemCount}/{itemCount}</p>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
                  <div className="h-full rounded-full bg-blue-600" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-[minmax(170px,1fr)_auto] sm:items-center">
                <select
                  value={getCanonicalPurchaseOrderStatus(order.status)}
                  onChange={(event) => handleStatusChange(event.target.value)}
                  disabled={savingStatus}
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm"
                >
                  {PURCHASE_ORDER_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center sm:justify-end">
                  {canAddMissingProduct && (
                    <button
                      type="button"
                      onClick={openAddItemsModal}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                    >
                      <FiPlus />
                      Agregar producto
                    </button>
                  )}
                  {canGenerateSale && (
                    <button
                      type="button"
                      onClick={async () => {
                        const approved = await confirm({
                          title: 'Generar venta',
                          message: 'Esto creara una venta a credito desde la orden y abrira el recibo.',
                          confirmLabel: 'Generar venta',
                          cancelLabel: 'Cancelar',
                          tone: 'warning',
                        });
                        if (approved) await handleConvertToSale(order);
                      }}
                      disabled={convertingSale}
                      className="h-10 rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {convertingSale ? 'Generando...' : 'Generar venta'}
                    </button>
                  )}
                  {hasGeneratedSale && (
                    <button
                      type="button"
                      onClick={() => handleConvertToSale(order)}
                      className="h-10 rounded-lg border border-emerald-200 bg-white px-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                    >
                      Ver recibo #{order.purchaseId}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="sticky top-16 z-10 rounded-xl border border-gray-200 bg-white/95 p-1 shadow-sm backdrop-blur">
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('review')}
              className={`flex min-h-[42px] items-center justify-center gap-2 rounded-lg text-sm font-semibold ${
                activeTab === 'review' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FiPackage />
              Revisar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`flex min-h-[42px] items-center justify-center gap-2 rounded-lg text-sm font-semibold ${
                activeTab === 'history' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FiList />
              Historial
            </button>
          </div>
        </div>

        {activeTab === 'review' ? (
          <>
            {renderRecentActivity()}
            <div className="space-y-2 sm:space-y-3">
              {items.map(renderItemCard)}
            </div>
            {order.allItemsConfirmed && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <div className="flex items-center gap-2 font-semibold">
                  <FiCheck />
                  {hasGeneratedSale
                    ? `Venta #${order.purchaseId} generada desde esta orden.`
                    : 'Todos los items estan confirmados.'}
                </div>
              </div>
            )}
            {renderOrderTotalsPanel()}
          </>
        ) : (
          renderHistoryTab()
        )}
      </div>
      {renderNumericPadModal()}
      {renderAllItemsDiscountModal()}
      {renderAddItemsModal()}
      <DiscountModal
        isOpen={Boolean(discountItem)}
        onClose={() => setDiscountItem(null)}
        variety={discountModalVariety}
        varietyPrice={discountModalPrice}
        handleDiscountChange={handleItemDiscountChange}
        discounts={discountMatrix}
        closeEverything={() => setDiscountItem(null)}
      />
      <OrderDiscountModal
        isOpen={showOrderDiscountModal}
        onClose={() => setShowOrderDiscountModal(false)}
        itemsSubtotal={Number(order.currentItemsSubtotal || 0)}
        orderDiscountAmount={Number(order.orderDiscountAmount || 0)}
        orderDiscountPercentage={order.orderDiscountPercentage ?? null}
        onApply={handleOrderDiscountApply}
      />
    </div>
  );
};

export default PurchaseOrderDetailPage;
